import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';
import { passwordRedirectUrl } from '../lib/appUrl';
import { authLinkErrorMessage } from '../lib/authLinkError';
import { NO_ACCESS_SENTENCE, signInErrorSentence } from '../lib/signInRefusal';
import { PRODUCT_NAME, PRODUCT_DESCRIPTOR, PRODUCT_STRAPLINE, TENANT_FALLBACK } from '../lib/tenant';

export default function Login() {
  const { signIn, accessDenied } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('login'); // 'login' | 'forgot'
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotError, setForgotError] = useState('');

  // #547, Session 79.14e — the auth error Supabase hands back, which was being
  // thrown away. The wording and the rules live in src/lib/authLinkError.js,
  // with the reasoning; this is the wiring that was missing.
  //
  // READ SYNCHRONOUSLY ON FIRST RENDER, deliberately. supabase-js parses and
  // CLEARS the fragment during its own startup, so a later read finds an empty
  // hash and this would quietly never fire — the same race SetPasswordPage
  // captures its recovery marker to avoid.
  const [linkError] = useState(() => authLinkErrorMessage(window.location.hash));

  // Drop the error fragment once it has been read, so a refresh does not repeat
  // a message about a link the person has already given up on. Only ever runs
  // for an ERROR fragment — a successful recovery carries `type=recovery` and no
  // `error`, and supabase-js still needs that one left alone.
  useEffect(() => {
    if (!linkError) return;
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  }, [linkError]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    const { error } = await signIn(email, password);
    if (error) {
      // #268's sign-out half, Session 296 — a locked sign-in says what the
      // gate says (src/lib/signInRefusal.js).
      setError(signInErrorSentence(error));
    } else {
      const destination = location.state?.from?.pathname || '/';
      navigate(destination, { replace: true });
    }
    setLoading(false);
  }

  async function handleForgotSubmit(e) {
    e.preventDefault();
    setForgotError('');
    setForgotSubmitting(true);
    // Issue #263 — the origin this page is served from, not a build-time variable.
    const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
      redirectTo: passwordRedirectUrl(),
    });
    setForgotSubmitting(false);
    if (error) {
      setForgotError(error.message);
      return;
    }
    setForgotSent(true);
  }

  return (
    <div className="login-wrap">
      <div className="login-card">
        {/* 74z8, brand identity gate. Three lines, nothing repeated: the name,
            what it does, the way in. An earlier draft stacked the strapline as
            text AND repeated it on the button — Colby cut it. The pun now lands
            exactly once, in the action position, where a pun survives repetition
            because a button is read as a control rather than as a slogan.
            The pronunciation is taught by the shape of the screen — Stede at the
            top, Get steady at the bottom, and the URL they typed to arrive. */}
        <div className="login-logo">
          <div className="login-logo-mark" aria-hidden="true">
            <svg viewBox="0 0 100 100" fill="none">
              <circle cx="50" cy="50" r="34" fill="none" stroke="#39D353" strokeWidth="8" />
              <line x1="5" y1="50" x2="19" y2="50" stroke="#39D353" strokeWidth="8" strokeLinecap="round" />
              <line x1="81" y1="50" x2="95" y2="50" stroke="#39D353" strokeWidth="8" strokeLinecap="round" />
              <circle cx="50" cy="50" r="13" fill="#39D353" />
            </svg>
          </div>
          <div className="login-title">{PRODUCT_NAME}</div>
          <div className="login-sub">{PRODUCT_DESCRIPTOR}</div>
        </div>

        {mode === 'login' ? (
          <>
            {/* #547 — above the form rather than inside it: this is about the
                link they arrived on, not about what they have just typed. */}
            {linkError && (
              <div className="login-error" style={{ marginBottom: '0.75rem' }} role="status">
                {linkError}
              </div>
            )}
            <form className="login-form" onSubmit={handleSubmit}>
              <div>
                <label className="login-label">Email address</label>
                <input
                  className="login-input"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>
              <div>
                <label className="login-label">Password</label>
                <input
                  className="login-input"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
              </div>
              {/* #268 — the credentials were correct and the sign-in was still
                  refused, so this cannot ride on `error`, which only ever
                  carries a Supabase auth failure. Deliberately does not say
                  which of the two reasons applied: to someone typing at the
                  login screen, "no profile row" and "deactivated" are the same
                  answer, and naming them tells an outsider whether an address
                  is real. */}
              {!error && accessDenied && (
                <div className="login-error">
                  {NO_ACCESS_SENTENCE}
                </div>
              )}
              {error && <div className="login-error">{error}</div>}
              <button className="login-btn" type="submit" disabled={loading}>
                {loading ? 'Signing in...' : PRODUCT_STRAPLINE}
              </button>
            </form>

            <div style={{ marginTop: '1rem', textAlign: 'center' }}>
              <button
                type="button"
                onClick={() => { setMode('forgot'); setForgotSent(false); setForgotError(''); setForgotEmail(email); }}
                style={{ background: 'none', border: 'none', color: '#39D353', fontSize: '12px', cursor: 'pointer', textDecoration: 'underline' }}
              >
                Forgot password?
              </button>
            </div>

            {/* The tenant's name, on the way in. Deliberately the build-time
                constant and not the live company_settings value: this screen
                renders before anyone is authenticated, and that table's SELECT
                policy is roles={'{authenticated}'} — anon genuinely cannot read
                it. Correct for a single-tenant deployment; a second customer
                needs either a per-tenant domain or an anon-readable settings
                view, which is a schema decision and not this file's to make. */}
            <div style={{ marginTop: '1rem', textAlign: 'center', fontSize: '11px', color: '#5C5C5C' }}>
              Contact your system administrator to get access. · {TENANT_FALLBACK}
            </div>

            {/* #442, Session 78o. Both documents were live and publicly readable
                and NO screen pointed at either — the same shape as #431 and
                #436: something built, working, and impossible to find.

                The href carries .html and the canonical URL does not, which is
                deliberate. Cloudflare Pages 308-redirects /privacy.html to
                /privacy, so a reader still lands on the clean URL — but an
                EXTENSIONLESS path does not resolve on the dev server, where it
                falls through to the SPA and returns the app with a 200. These
                links were first shipped extensionless, verified against
                production, and were dead on localhost for exactly that reason.
                CORRECTED Session 78o after Colby clicked one.

                That is #442's own trap met from the other side: Cloudflare falls
                back to the app for unknown paths, so a wrong path renders a
                sign-in screen with a 200 rather than a 404 — a broken link that
                looks perfectly healthy. It is why these are hard-coded rather
                than built from a variable, and why the check that proves them
                has to visit a deliberately WRONG path too.

                The sign-in screen is the deliberate placement: it is where
                somebody deciding whether to trust this app will look, and it is
                reachable without an account. */}
            <div style={{ marginTop: '0.6rem', textAlign: 'center', fontSize: '11px' }}>
              <a href="/privacy.html" style={{ color: '#5C5C5C' }}>Privacy</a>
              <span style={{ color: '#C0C0C0', margin: '0 8px' }}>·</span>
              <a href="/terms.html" style={{ color: '#5C5C5C' }}>Terms</a>
            </div>
          </>
        ) : forgotSent ? (
          <div style={{ textAlign: 'center' }}>
            <div className="login-title" style={{ fontSize: '16px' }}>Check your email</div>
            <div style={{ marginTop: '0.75rem', fontSize: '13px', color: '#5C5C5C' }}>
              If an account exists for {forgotEmail}, a password reset link is on its way.
            </div>
            <button
              type="button"
              onClick={() => setMode('login')}
              style={{ marginTop: '1.5rem', background: 'none', border: 'none', color: '#39D353', fontSize: '12px', cursor: 'pointer', textDecoration: 'underline' }}
            >
              Back to sign in
            </button>
          </div>
        ) : (
          <form className="login-form" onSubmit={handleForgotSubmit}>
            <div>
              <label className="login-label">Email address</label>
              <input
                className="login-input"
                type="email"
                placeholder="you@example.com"
                value={forgotEmail}
                onChange={e => setForgotEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>
            {forgotError && <div className="login-error">{forgotError}</div>}
            <button className="login-btn" type="submit" disabled={forgotSubmitting}>
              {forgotSubmitting ? 'Sending...' : 'Send reset link'}
            </button>
            <div style={{ marginTop: '1rem', textAlign: 'center' }}>
              <button
                type="button"
                onClick={() => setMode('login')}
                style={{ background: 'none', border: 'none', color: '#5C5C5C', fontSize: '12px', cursor: 'pointer', textDecoration: 'underline' }}
              >
                Back to sign in
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}